import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Camera, Upload, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Block } from "@/types/proposals";

interface DynamicImageBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  isEditing?: boolean;
  jobId?: string;
}

export function DynamicImageBlock({ block, onUpdate, isEditing, jobId }: DynamicImageBlockProps) {
  const [showModal, setShowModal] = useState(false);
  const [jobPhotos, setJobPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const images = block.content?.images || [];
  const maxImages = block.settings?.maxImages || 4;
  const style = block.style || {};
  const blockSize = block.settings?.blockSize || 'medium';
  const textAlign = block.style?.textAlign || 'left';

  const containerAlignClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }[textAlign];

  const sizeClasses = {
    small: { 
      container: 'max-w-[300px] min-h-[150px]', 
      imageHeight: '8rem' 
    },
    medium: { 
      container: 'max-w-[500px] min-h-[200px]', 
      imageHeight: '12rem' 
    },
    large: { 
      container: 'max-w-full min-h-[300px]', 
      imageHeight: '16rem' 
    },
  }[blockSize];

  const sizeClass = sizeClasses.container;
  const imageHeight = sizeClasses.imageHeight;

  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      const canvas = document.createElement('canvas');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')?.drawImage(video, 0, 0);
      
      stream.getTracks().forEach(track => track.stop());

      canvas.toBlob(async (blob) => {
        if (blob && jobId) {
          const fileName = `${Date.now()}.jpg`;
          const { data, error } = await supabase.storage
            .from('job-photos')
            .upload(`${jobId}/${fileName}`, blob);

          if (error) throw error;

          const { data: { publicUrl } } = supabase.storage
            .from('job-photos')
            .getPublicUrl(data.path);

          onUpdate({
            content: {
              ...block.content,
              images: [...images, publicUrl]
            }
          });
          toast.success("Photo captured and added");
          setShowModal(false);
        }
      });
    } catch (error) {
      console.error('Camera error:', error);
      toast.error("Failed to access camera");
    }
  };

  const loadJobPhotos = async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('photos')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobPhotos(data || []);
    } catch (error) {
      console.error('Error loading photos:', error);
      toast.error("Failed to load job photos");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectJobPhoto = (photoUrl: string) => {
    if (images.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }
    
    onUpdate({
      content: {
        ...block.content,
        images: [...images, photoUrl]
      }
    });
    toast.success("Photo added");
    setShowModal(false);
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_: any, i: number) => i !== index);
    onUpdate({
      content: {
        ...block.content,
        images: newImages
      }
    });
  };

  return (
    <div 
      className={`flex ${containerAlignClass}`}
    >
      <Card className={`${sizeClass} p-6 border-2 border-dashed hover:border-primary/50 transition-colors`}>
        <div className="text-center">
          {images.length === 0 ? (
            <>
              <ImageIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                {isEditing ? "Click to add photos" : "Photos will appear here"}
              </p>
              {isEditing && (
                <Button
                  onClick={() => {
                    setShowModal(true);
                    loadJobPhotos();
                  }}
                  variant="outline"
                  size="sm"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Add Photos
                </Button>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {images.map((img: string, idx: number) => (
                <div key={idx} className="relative group">
                  <img
                    src={img}
                    alt={`Photo ${idx + 1}`}
                    style={{
                      width: 'auto',
                      maxWidth: '100%',
                      maxHeight: imageHeight,
                      objectFit: 'contain',
                      borderRadius: style.borderRadius ? `${style.borderRadius}px` : undefined,
                      clipPath: style.clipPath,
                    }}
                  />
                  {isEditing && (
                    <Button
                      size="sm"
                      variant="destructive"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100"
                      onClick={() => handleRemoveImage(idx)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              {isEditing && images.length < maxImages && (
                <Button
                  onClick={() => {
                    setShowModal(true);
                    loadJobPhotos();
                  }}
                  variant="outline"
                  className="h-48"
                >
                  <Camera className="w-6 h-6 mr-2" />
                  Add More
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Photos</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="camera">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="camera">
                <Camera className="w-4 h-4 mr-2" />
                Camera
              </TabsTrigger>
              <TabsTrigger value="upload">
                <Upload className="w-4 h-4 mr-2" />
                Job Photos
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="camera" className="space-y-4">
              <div className="text-center py-8">
                <Camera className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-4">
                  Capture a photo using your device camera
                </p>
                <Button onClick={handleCameraCapture}>
                  <Camera className="w-4 h-4 mr-2" />
                  Open Camera
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="upload" className="space-y-4">
              {loading ? (
                <div className="text-center py-8">Loading photos...</div>
              ) : jobPhotos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No photos uploaded to this job yet
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                  {jobPhotos.map((photo) => (
                    <div
                      key={photo.id}
                      className="cursor-pointer hover:opacity-75 transition-opacity"
                      onClick={() => handleSelectJobPhoto(photo.url)}
                    >
                      <img
                        src={photo.url}
                        alt="Job photo"
                        className="w-full h-32 object-cover rounded"
                      />
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}